import { useIntl } from 'react-intl';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  COHORT_ACTIONS,
} from '@verantos-dev/app-state-research-assistant';
import { MessageLegacy, ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-cohorts';

type Message = {
  userPrompt?: string;
  summary?: string;
  createdAt: string;
  modelVersion: string;
  isUserGeneratedDsl?: boolean;
  cohortAction?: string;
  dsl?: string;
};

export const useHandlePopulateChatHistory = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const constructMessages = (messages: Array<Message>) => {
    return messages.reduce<Array<MessageLegacy>>((acc, message) => {
      const lastMessage = acc.slice(-1)[0];
      if (
        lastMessage &&
        lastMessage.modelVersion &&
        lastMessage.modelVersion !== message.modelVersion
      ) {
        const textToDisplay = intl.formatMessage(
          {
            id: 'projects.cohortBuilderPage.researchAssistant.updateModelVersionInfoMessage',
          },
          {
            oldModelVersion: lastMessage.modelVersion,
            newModelVersion: message.modelVersion,
          }
        );
        acc.push({
          message: textToDisplay,
          timestamp: message.createdAt,
          currentUser: '',
          isCurrentUser: false,
          type: ResearchAssistantMessageEnumLegacy.MODEL_CHANGE,
          modelVersion: message.modelVersion,
        });
      }
      if (!message.isUserGeneratedDsl && !message.cohortAction) {
        if (message.userPrompt) {
          acc.push({
            message: message.userPrompt,
            timestamp: message.createdAt,
            currentUser: 'user',
            isCurrentUser: false,
            modelVersion: message.modelVersion,
          });
        }
        if (message.summary) {
          acc.push({
            message: message.summary || '',
            timestamp: message.createdAt,
            currentUser: 'research_assistant',
            isCurrentUser: false,
            modelVersion: message.modelVersion,
          });
        }
      }
      if (message.cohortAction) {
        if (message.cohortAction === COHORT_ACTIONS.SAVE) {
          const textToDisplay = intl.formatMessage({
            id: 'projects.cohortBuilderPage.researchAssistant.savedInfoMessage',
          });
          if (lastMessage && lastMessage.message === textToDisplay) {
            acc[acc.length - 1].timestamp = message.createdAt;
          } else {
            acc.push({
              message: textToDisplay,
              timestamp: message.createdAt,
              currentUser: '',
              isCurrentUser: false,
              type: ResearchAssistantMessageEnumLegacy.SAVE,
              modelVersion: message.modelVersion,
            });
          }
        }
        if (message.cohortAction === COHORT_ACTIONS.PROCESS) {
          const textToDisplay = intl.formatMessage({
            id: 'projects.cohortBuilderPage.researchAssistant.processedInfoMessage',
          });
          if (lastMessage && lastMessage.message === textToDisplay) {
            acc[acc.length - 1].timestamp = message.createdAt;
          } else {
            acc.push({
              message: textToDisplay,
              timestamp: message.createdAt,
              currentUser: '',
              isCurrentUser: false,
              type: ResearchAssistantMessageEnumLegacy.PROCESS,
              modelVersion: message.modelVersion,
            });
          }
        }
      }
      if (message.isUserGeneratedDsl) {
        const textToDisplay = intl.formatMessage({
          id: 'projects.cohortBuilderPage.researchAssistant.updateInfoMessage',
        });
        if (lastMessage && lastMessage.message === textToDisplay) {
          acc[acc.length - 1].timestamp = message.createdAt;
        } else {
          acc.push({
            message: textToDisplay,
            timestamp: message.createdAt,
            currentUser: '',
            isCurrentUser: false,
            type: ResearchAssistantMessageEnumLegacy.INFO,
            modelVersion: message.modelVersion,
          });
        }
      }
      return acc;
    }, []);
  };

  const populateMessages = (messages: Array<Message>) => {
    dispatch(addResearchAssistantMessages(constructMessages(messages)));
  };

  return { populateMessages };
};
